import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { type ChatTypingUser } from '@apps/web/hooks/use_phoebe_chat_typing';
import { getApiClient } from '@apps/web/lib/api_client';

// Throttle "still typing" pings so a fast typist sends at most one POST per
// window, and auto-clear a remote typer shortly after their last ping so a
// dropped client never leaves a stuck "is typing…" indicator.
const TYPING_RESEND_INTERVAL_MS = 3000;
const TYPING_STOP_DEBOUNCE_MS = 3000;
const TYPING_EXPIRY_MS = 5000;
const TYPING_SWEEP_INTERVAL_MS = 1000;

type TypingEntry = {
    readonly displayName: string;
    readonly expiresAt: number;
};

export function formatTypingLabel(
    typingUsers: ReadonlyArray<{ readonly displayName: string }>,
): string {
    const names = typingUsers.map((typingUser) => typingUser.displayName);
    if (names.length === 0) {
        return '';
    }
    if (names.length === 1) {
        return `${names[0]} is typing…`;
    }
    if (names.length === 2) {
        return `${names[0]} and ${names[1]} are typing…`;
    }
    const otherCount = names.length - 2;
    return `${names[0]}, ${names[1]}, and ${otherCount} ${
        otherCount === 1 ? 'other' : 'others'
    } are typing…`;
}

export function useConversationTyping({
    organizationId,
    conversationId,
    currentUserId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly conversationId: string | null;
    readonly currentUserId: string | null;
    readonly enabled: boolean;
}): {
    readonly typingUsers: Array<ChatTypingUser>;
    readonly notifyTyping: () => void;
} {
    const [typingByUserId, setTypingByUserId] = useState<
        Record<string, TypingEntry>
    >({});
    const lastTrueSentAtMsRef = useRef(0);
    const stopTimerRef = useRef<number | null>(null);

    const sendTyping = useCallback(
        (typing: boolean) => {
            if (!enabled || !organizationId || !conversationId) {
                return;
            }
            void getApiClient()
                .post({
                    url: '/organizations/{organization_id}/conversations/{conversation_id}/typing',
                    pathParams: {
                        organization_id: organizationId,
                        conversation_id: conversationId,
                    },
                    body: { typing },
                })
                .catch(() => {
                    // Presence is best-effort; a dropped ping self-heals via the
                    // remote expiry sweep, so failures are intentionally ignored.
                });
        },
        [enabled, organizationId, conversationId],
    );

    const notifyTyping = useCallback(() => {
        if (!enabled || !organizationId || !conversationId) {
            return;
        }
        const nowMs = Date.now();
        if (nowMs - lastTrueSentAtMsRef.current > TYPING_RESEND_INTERVAL_MS) {
            lastTrueSentAtMsRef.current = nowMs;
            sendTyping(true);
        }
        if (stopTimerRef.current !== null) {
            window.clearTimeout(stopTimerRef.current);
        }
        stopTimerRef.current = window.setTimeout(() => {
            lastTrueSentAtMsRef.current = 0;
            sendTyping(false);
        }, TYPING_STOP_DEBOUNCE_MS);
    }, [enabled, organizationId, conversationId, sendTyping]);

    // Announce that we stopped typing when the thread unmounts or changes, so
    // teammates don't see a lingering indicator.
    useEffect(() => {
        return () => {
            if (stopTimerRef.current !== null) {
                window.clearTimeout(stopTimerRef.current);
                stopTimerRef.current = null;
            }
            if (lastTrueSentAtMsRef.current !== 0) {
                lastTrueSentAtMsRef.current = 0;
                sendTyping(false);
            }
        };
    }, [sendTyping]);

    useListenForOrganizationEvent(
        'conversation_typing',
        (event) => {
            if (event.type !== 'conversation_typing') {
                return;
            }
            const data = event.data;
            if (
                data.conversation_id !== conversationId ||
                data.user_id === currentUserId
            ) {
                return;
            }
            setTypingByUserId((previous) => {
                const next = { ...previous };
                if (data.typing) {
                    next[data.user_id] = {
                        displayName: data.display_name,
                        expiresAt: Date.now() + TYPING_EXPIRY_MS,
                    };
                } else {
                    delete next[data.user_id];
                }
                return next;
            });
        },
        { organizationId },
    );

    // Reset presence when the viewed thread (or org) changes, so typers from a
    // previously-open thread don't linger until their TTL expires. No-op (same
    // reference) when already empty so this doesn't force an extra render.
    useEffect(() => {
        setTypingByUserId((previous) =>
            Object.keys(previous).length === 0 ? previous : {},
        );
    }, [conversationId, organizationId]);

    // Drop expired typers so a missed "stopped" ping cannot wedge the indicator.
    // Gate the sweep on whether ANY typer is present (a boolean) so the interval
    // ticks steadily instead of restarting on every individual expiry.
    const hasTypers = Object.keys(typingByUserId).length > 0;
    useEffect(() => {
        if (!hasTypers) {
            return;
        }
        const intervalId = window.setInterval(() => {
            setTypingByUserId((previous) => {
                const nowMs = Date.now();
                const remaining = Object.fromEntries(
                    Object.entries(previous).filter(
                        ([, entry]) => entry.expiresAt > nowMs,
                    ),
                );
                return Object.keys(remaining).length ===
                    Object.keys(previous).length
                    ? previous
                    : remaining;
            });
        }, TYPING_SWEEP_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasTypers]);

    const typingUsers = useMemo(
        () =>
            // Exclude self at render time as well as in the listener: currentUserId
            // can be null while useUser resolves, so an own-typing event arriving
            // in that window would otherwise slip past the listener filter.
            Object.entries(typingByUserId)
                .filter(([userId]) => userId !== currentUserId)
                .map(([userId, entry]) => ({
                    userId,
                    displayName: entry.displayName,
                })),
        [typingByUserId, currentUserId],
    );

    return { typingUsers, notifyTyping };
}

// Read-only typing presence for a whole conversation list, keyed by
// conversation id. The inbox list is not virtualized and can hold many rows,
// so one panel-level subscription feeds every row instead of mounting a
// per-conversation `useConversationTyping` (and its own org-event listener) on
// each. Rows that no one is typing in are absent from the map, so a row reads
// its typers with `typingUsersByConversation[conversationId] ?? []`.
export function useOrganizationConversationTyping({
    organizationId,
    currentUserId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly currentUserId: string | null;
    readonly enabled: boolean;
}): Record<string, Array<ChatTypingUser>> {
    const [typingByConversation, setTypingByConversation] = useState<
        Record<string, Record<string, TypingEntry>>
    >({});

    useListenForOrganizationEvent(
        'conversation_typing',
        (event) => {
            if (!enabled || event.type !== 'conversation_typing') {
                return;
            }
            const data = event.data;
            if (data.user_id === currentUserId) {
                return;
            }
            setTypingByConversation((previous) => {
                const conversationTypers = {
                    ...(previous[data.conversation_id] ?? {}),
                };
                if (data.typing) {
                    conversationTypers[data.user_id] = {
                        displayName: data.display_name,
                        expiresAt: Date.now() + TYPING_EXPIRY_MS,
                    };
                } else {
                    delete conversationTypers[data.user_id];
                }
                const next = { ...previous };
                if (Object.keys(conversationTypers).length === 0) {
                    delete next[data.conversation_id];
                } else {
                    next[data.conversation_id] = conversationTypers;
                }
                return next;
            });
        },
        { organizationId },
    );

    // Clear presence when the org changes so typers from a previous org don't
    // linger until their TTL expires. No-op (same reference) when already empty.
    useEffect(() => {
        setTypingByConversation((previous) =>
            Object.keys(previous).length === 0 ? previous : {},
        );
    }, [organizationId]);

    // Drop expired typers across every conversation so a missed "stopped" ping
    // cannot wedge a row's indicator. Gate on whether ANY typer is present so
    // the interval ticks steadily instead of restarting on each expiry.
    const hasTypers = Object.keys(typingByConversation).length > 0;
    useEffect(() => {
        if (!hasTypers) {
            return;
        }
        const intervalId = window.setInterval(() => {
            setTypingByConversation((previous) => {
                const nowMs = Date.now();
                let changed = false;
                const next: Record<string, Record<string, TypingEntry>> = {};
                for (const [conversationId, typers] of Object.entries(
                    previous,
                )) {
                    const remaining = Object.fromEntries(
                        Object.entries(typers).filter(
                            ([, entry]) => entry.expiresAt > nowMs,
                        ),
                    );
                    if (
                        Object.keys(remaining).length !==
                        Object.keys(typers).length
                    ) {
                        changed = true;
                    }
                    if (Object.keys(remaining).length > 0) {
                        next[conversationId] = remaining;
                    }
                }
                return changed ? next : previous;
            });
        }, TYPING_SWEEP_INTERVAL_MS);
        return () => window.clearInterval(intervalId);
    }, [hasTypers]);

    return useMemo(() => {
        const result: Record<string, Array<ChatTypingUser>> = {};
        for (const [conversationId, typers] of Object.entries(
            typingByConversation,
        )) {
            // Exclude self here too: currentUserId can be null while useUser
            // resolves, so an own-typing event in that window slips the listener.
            const users = Object.entries(typers)
                .filter(([userId]) => userId !== currentUserId)
                .map(([userId, entry]) => ({
                    userId,
                    displayName: entry.displayName,
                }));
            if (users.length > 0) {
                result[conversationId] = users;
            }
        }
        return result;
    }, [typingByConversation, currentUserId]);
}
